/**
 * MembershipResignationPort — the web seam for the member resignation
 * (eroilmoitus) write (056).
 *
 * A DEDICATED port, not a method on `MessageComposePort` (owner decision
 * 2026-10-02): resignation is not a message (D-235). The compose wizard hosts
 * the step; it does not make resignation a compose operation.
 *
 * The outcome mirrors the server's `SubmitResignationMutationResult`, not the
 * server port's union: the internal effect and the raw problem type never
 * reach the browser.
 */

/** Why a submission did not complete. Copy is chosen by this reason, not by `status` alone. */
export type ResignationRefusal =
  | 'already-pending'
  | 'not-a-member'
  | 'refused'
  | 'retry-conflict'
  | 'unavailable';

export type SubmitResignationOutcome =
  /** `submittedAt` is the register's own time, relayed verbatim — never minted here. */
  | { readonly status: 'ok'; readonly submittedAt: string }
  /** Accepted by the register, but the server could not audit it: shown as "could not confirm". */
  | { readonly status: 'unconfirmed' }
  | { readonly status: 'refused'; readonly reason: ResignationRefusal };

/**
 * One submit intent. Its key is minted ONCE and re-sent on every attempt of the
 * same intent, including a retry after an unresolved outcome (owner decision
 * 2026-10-02): if the first attempt did land, the Integration Service replays
 * its original response within its 24 h dedup window, and after that the
 * register answers "already pending" — either way no second resignation.
 */
export interface ResignationIntent {
  readonly idempotencyKey: string;
}

/** Mint a fresh submit intent. The UUID satisfies the router's `[A-Za-z0-9-]{8,128}`. */
export function newResignationIntent(): ResignationIntent {
  return { idempotencyKey: crypto.randomUUID() };
}

export interface MembershipResignationPort {
  /**
   * Submit the signed-in member's resignation. Resolves to a value for every
   * outcome a member can meet; rejects only on a composition error (the
   * namespace is not mounted).
   */
  submitResignation(intent: ResignationIntent): Promise<SubmitResignationOutcome>;
}
